import React from 'react';
import {
  Paper,
  Typography,
  List,
  Box,
  Chip,
  IconButton,
  Stack,
  ListItem
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Visit } from '../types';

interface FollowUpTrackerProps {
  visits: Visit[];
}

const mockFollowUps = [
  {
    id: 1,
    studentName: 'John Smith',
    action: 'Provide tutoring resources',
    dueDate: '2023-12-25',
    status: 'pending',
    priority: 'high'
  },
  {
    id: 2,
    studentName: 'Jane Doe',
    action: 'Schedule counseling session',
    dueDate: '2023-12-20',
    status: 'completed',
    priority: 'medium'
  },
  {
    id: 3,
    studentName: 'Michael Johnson',
    action: 'Review attendance improvement plan',
    dueDate: '2023-12-22',
    status: 'pending',
    priority: 'high'
  },
  {
    id: 4,
    studentName: 'Sarah Williams',
    action: 'Follow up on medical documentation',
    dueDate: '2023-12-21',
    status: 'pending',
    priority: 'medium'
  }
];

export const FollowUpTracker: React.FC<FollowUpTrackerProps> = () => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Paper sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="h2">
          Follow-Up Actions
        </Typography>
      </Box>

      <List sx={{ 
        flex: 1,
        overflow: 'auto',
        p: 0
      }}>
        {mockFollowUps.map((item) => (
          <ListItem
            key={item.id}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              py: 2,
              px: 2,
              '&:last-child': {
                borderBottom: 0
              }
            }}
          >
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{ justifyContent: 'space-between' }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {item.status === 'completed' ? (
                    <CompleteIcon color="success" fontSize="small" />
                  ) : (
                    <PendingIcon color="warning" fontSize="small" />
                  )}
                  <Typography variant="subtitle2">
                    {item.studentName}
                  </Typography>
                  <Chip
                    label={item.priority}
                    color={getPriorityColor(item.priority)}
                    size="small"
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton 
                    size="small" 
                    aria-label="edit"
                    sx={{ color: 'action.active' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    aria-label="delete"
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body2">
                  {item.action}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Due: {new Date(item.dueDate).toLocaleDateString()}
                </Typography>
              </Stack>
            </Stack>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};